import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import FilesPreviewer from 'modules/expenses/components/expenses/Detail/_FilesPreviewer'
import NewExpenseForm from 'modules/expenses/components/expenses/Detail/_Form/Expense/new'
import { getExpensesFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { ExpenseSchema } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/schema'
import Sidepanel from 'modules/expenses/components/expenses/Detail/_Sidepanel'
import { BulkActionsProvider } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { FilesPreviewerProvider } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import CreateExpenseFooterActions from 'modules/expenses/components/expenses/New/NewForm/CreateExpenseFooterActions'
import { normalizeEffectiveOn } from 'modules/expenses/hooks/useExpensable'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import useNewExpensesPage from 'modules/expenses/hooks/useNewExpensesPage'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import type { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { filterCustomizationFieldsByExpenseDocumentType } from 'modules/expenses/lib/customization_settings/filterByExpenseDocumentType'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

export interface NewManualExpensesFormProps {
  customizationFields: CustomizationField[]
  hasProjectsFeature: boolean
  canSeeEmployeeLegalEntity: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  canCustomizeSettings: boolean
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  hasCostCentersFeature: boolean
  groupCurrency?: string | null
  onClose: () => void
}

const NewManualExpensesForm = ({
  customizationFields: unfilteredCustomizationFields,
  hasProjectsFeature,
  canSeeEmployeeLegalEntity,
  currencies,
  canCustomizeSettings,
  hasReportOnBehalfFeature,
  canCreateExpensesForOthers,
  hasCostCentersFeature,
  groupCurrency,
  onClose,
}: NewManualExpensesFormProps) => {
  const [formEffectiveOn, setFormEffectiveOn] = useState('')
  const [formEmployeeId, setFormEmployeeId] = useState<number>()
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [_openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [documentType, setDocumentType] = useState<string | undefined>(undefined)

  const { employee } = useCurrentSession()
  const { loading } = useNewExpensesPage()
  const { groupId } = useParams()
  const employeeId = formEmployeeId ?? employee?.id

  const customizationFields = useUnsafeMemo(
    () =>
      filterCustomizationFieldsByExpenseDocumentType(unfilteredCustomizationFields, documentType),
    [unfilteredCustomizationFields, documentType]
  )

  const defaultValues = getExpensesFormDefaultValues({
    employee,
    customizationFields,
    groupId: groupId ? Number(groupId) : null,
    groupCurrency,
  })

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
  })

  const { taxAgencyOrganizationId } = useOutletContext<RouteContextParams>()

  const expenseForm = useForm<ExpenseSchema>({
    mode: 'onChange',
    resolver: zodResolver(
      ExpenseSchema({
        customizationFields,
        hasSignedDocuments: false,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        resourceType: 'expense',
        hasCostCentersFeature,
      })
    ),
    defaultValues: {
      ...defaultValues,
      files: [],
    },
  })

  const { trigger } = expenseForm

  useUnsafeEffect(() => {
    trigger()
  }, [customizationFields, trigger])

  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }

  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }

  const onDocumentTypeChange = (value: string) => {
    setDocumentType(value)
  }

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }

  if (loading) return <Loading inline />

  return (
    <BulkActionsProvider>
      <Sidepanel
        open={openSidepanel}
        onClose={handleClose}
        onCloseClicked={handleCloseClicked}
        canRestartApprovalFlow={false}
      >
        <FilesPreviewerProvider initialFiles={[]}>
          <FilesPreviewer onClose={handleClose} onDrop={onDropHandler} showUploadButton />
          <Form padding="s0" notRounded>
            <FormProvider {...expenseForm}>
              <Box flexGrow gap="s16" paddingX="s24">
                <NewExpenseForm
                  customizationFields={customizationFields}
                  canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                  currencies={currencies}
                  canCustomizeSettings={canCustomizeSettings}
                  canCreateExpensesForOthers={canCreateExpensesForOthers}
                  hasProjectsFeature={hasProjectsFeature}
                  taxAgencyOrganization={taxAgencyOrganizationId}
                  projectWorkers={newProjectWorkers}
                  budgetOptions={newBudgetOptions}
                  loadingProjectWorkers={newLoadingProjectWorkers}
                  loadingBudgetOptions={newLoadingBudgetOptions}
                  onDocumentTypeChange={onDocumentTypeChange}
                  onEmployeeIdChange={setFormEmployeeId}
                  onEffectiveOnChange={onEffectiveOnChange}
                  hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                  canEditExpensesWithPendingStatus={false}
                  hasCostCentersFeature={hasCostCentersFeature}
                  registerDropHandler={setOnDropHandler}
                />
              </Box>
              <CreateExpenseFooterActions
                formType="expense"
                customizationFields={customizationFields}
                onClose={handleClose}
                groupId={groupId}
                taxAgencyOrganization={taxAgencyOrganizationId}
              />
            </FormProvider>
          </Form>
        </FilesPreviewerProvider>
      </Sidepanel>
    </BulkActionsProvider>
  )
}

export default NewManualExpensesForm
